import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import AllReviewsSection from '../components/AllReviewsSection';
import IntensityIndicator from '../components/IntensityIndicator';
import TrustBadges from '../components/TrustBadges';
import QuickMetrics from '../components/QuickMetrics';
import EducationalCarousel from '../components/EducationalCarousel';
import MoodProductsSection from '../components/MoodProductsSection';
import NeonBanner from '../components/NeonBanner';
import { productPath } from '../utils/helpers';
import { SoldOutRibbon, ProductStamp } from '../components/design-system';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Video URL for hero button

// Default background image (fallback)
const DEFAULT_HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/089rqmjp_Gemini_Generated_Image_bwp777bwp777bwp7.png';

// Category images
const UOMO_IMAGE = 'https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/u0eahmew_boton-OUMO2.jpg';
const DONNE_IMAGE = 'https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/lxt9e90p_boton-DONNE2.jpg';

// Discovery categories - UPDATED to use new filter system
const discoveryCategories = [
  { id: 'citrico', title: '🍋 Cítrico', filter: 'olfactory_family=Cítrico' },
  { id: 'fresco', title: '🌊 Fresco', filter: 'olfactory_family=Fresco' },
  { id: 'dulce', title: '🍬 Dulce', filter: 'olfactory_family=Dulce' },
  { id: 'madera', title: '🪵 Madera', filter: 'olfactory_family=Madera' },
  { id: 'floral', title: '🌸 Floral', filter: 'olfactory_family=Floral' },
];

// Brand Product Card Component (antes NeonProductCard — nombre conservado para no romper referencias)
const NeonProductCard = ({ product }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  // Determine name color based on gender
  const getNameColorClass = () => {
    if (product.gender === 'Hombre') return 'text-brand-cobalt group-hover:text-brand-cobalt/80';
    if (product.gender === 'Mujer') return 'text-brand-magenta group-hover:text-brand-magenta/80';
    return 'text-brand-magenta group-hover:text-brand-magenta/80'; // Unisex defaults to magenta
  };

  // Determine hover accent based on gender (para hybrid-glow)
  const getHoverShadowClass = () => {
    if (product.gender === 'Mujer') return 'magenta-accent';
    return ''; // Default cobalt glow via CSS
  };

  const isOutOfStock = product.stock === 0 && product.stock_40ml === 0;
  
  return (
    <Link
      to={productPath(product)}
      className="flex-shrink-0 w-36 md:w-44 group"
    >
      <div className={`brand-card hybrid-glow rounded-lg overflow-hidden p-2 transition-all duration-300 h-full ${getHoverShadowClass()}`}>
        <div className="relative aspect-square bg-brand-black/5 rounded overflow-hidden mb-2">
          <img
            src={product.main_image || 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=400'}
            alt={product.name}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isOutOfStock ? 'opacity-60' : ''}`}
            loading="lazy"
          />
          <ProductStamp />
          {/* Badge de etiquetas - Agotado tiene prioridad */}
          {isOutOfStock ? (
            <SoldOutRibbon />
          ) : product.popularity?.includes('Nuevo Lanzamiento') ? (
            <span className="absolute top-1.5 left-1.5 bg-brand-cobalt text-white text-[7px] px-1 py-0.5 uppercase tracking-wider font-medium rounded-sm">
              Nuevo
            </span>
          ) : product.popularity?.includes('Más vendidos') ? (
            <span className="absolute top-1.5 left-1.5 bg-brand-magenta text-white text-[7px] px-1 py-0.5 uppercase tracking-wider font-medium rounded-sm">
              Más vendido
            </span>
          ) : null}
        </div>
        {/* Info container with fixed height */}
        <div className="flex flex-col" style={{ minHeight: '110px' }}>
          <div>
            {/* Código - Negro, negritas */}
            <p className="text-xs font-bold text-brand-black truncate">
              {product.product_code}
            </p>
            {/* Inspirado en: - pequeño */}
            <p className="text-[8px] text-brand-black/50">Inspirado en:</p>
            {/* Nombre - Color según género */}
            <h4 className={`text-sm font-bold truncate transition-colors ${getNameColorClass()}`} title={product.inspired_by_name}>
              {product.inspired_by_name}
            </h4>
            {/* Marca */}
            <p className="text-xs text-brand-black/70 truncate">
              {product.inspired_by_brand}
            </p>
          </div>
          {/* Bottom section */}
          <div className="mt-auto pt-1">
            {/* Precio */}
            <p className="text-xs font-medium text-brand-black">
              {formatPrice(product.price_40ml || 250)}
            </p>
            {/* Intensity Indicator */}
            <div className="h-5">
              {product.intensity && (
                <IntensityIndicator intensity={product.intensity} size="sm" />
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Product Row Component (Netflix style)
const ProductRow = ({ category, products, loading }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  if (loading) {
    return (
      <div className="mb-8">
        <h3 className="text-lg md:text-xl font-display mb-4 px-4 text-brand-black">{category.title}</h3>
        <div className="flex gap-4 px-4 overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-40 md:w-48 animate-pulse">
              <div className="aspect-square bg-brand-cobalt/10 rounded-lg mb-2"></div>
              <div className="h-3 bg-brand-cobalt/10 rounded w-3/4 mb-1"></div>
              <div className="h-4 bg-brand-cobalt/10 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) return null;

  return (
    <div className="mb-8 md:mb-10 relative group/row">
      <div className="flex items-center justify-between mb-3 md:mb-4 px-4">
        <h3 className="text-lg md:text-xl font-display font-semibold text-brand-black">{category.title}</h3>
        <Link 
          to={`/products?${category.filter}`}
          className="text-xs md:text-sm text-brand-cobalt hover:underline flex items-center gap-1"
        >
          Ver todos <ArrowRight size={14} />
        </Link>
      </div>
      
      <div className="relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-brand-bone border border-brand-cobalt/20 rounded-full opacity-0 group-hover/row:opacity-100 transition-opacity hidden md:flex text-brand-cobalt"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        
        {/* Products Scroll */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide px-4 pb-2 scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <NeonProductCard key={product.id} product={product} />
          ))}
        </div>
        
        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-brand-bone border border-brand-cobalt/20 rounded-full opacity-0 group-hover/row:opacity-100 transition-opacity hidden md:flex text-brand-cobalt"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>
    </div>
  );
};

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [topHombreProducts, setTopHombreProducts] = useState([]);
  const [topMujerProducts, setTopMujerProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topLoading, setTopLoading] = useState(true);
  const [discoveryProducts, setDiscoveryProducts] = useState({});
  const [discoveryLoading, setDiscoveryLoading] = useState(true);
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    loadHeroSlides();
    loadFeaturedProducts();
    loadTopProducts();
    loadDiscoveryProducts();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 6000); // Change slide every 6 seconds
    
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const loadHeroSlides = async () => {
    try {
      // El Hero mezcla los banners normales ("home_hero") con los de Flash Sale
      // ("flash_sale") en un solo carrusel, ordenados todos por su campo "order".
      const [heroRes, flashRes] = await Promise.all([
        fetch(`${API_URL}/api/public/banners?position=home_hero`),
        fetch(`${API_URL}/api/public/banners?position=flash_sale`)
      ]);
      const heroData = heroRes.ok ? await heroRes.json() : { banners: [] };
      const flashData = flashRes.ok ? await flashRes.json() : { banners: [] };

      const activeBanners = [...(heroData.banners || []), ...(flashData.banners || [])]
        .filter(b => b.is_active)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      if (activeBanners.length > 0) {
        // Preload first image/video
        const firstBanner = activeBanners[0];
        if (firstBanner.media_type === 'video') {
          setHeroSlides(activeBanners);
          setHeroLoaded(true);
        } else {
          const img = new Image();
          img.onload = () => {
            setHeroSlides(activeBanners);
            setHeroLoaded(true);
          };
          img.onerror = () => {
            setHeroLoaded(true);
          };
          img.src = firstBanner.image_url || firstBanner.media_url;
        }
      } else {
        // No banners, use default
        setHeroSlides([{ id: 'default', media_type: 'image', image_url: DEFAULT_HERO_IMAGE }]);
        setHeroLoaded(true);
      }
    } catch (error) {
      console.error('Error loading hero slides:', error);
      setHeroSlides([{ id: 'default', media_type: 'image', image_url: DEFAULT_HERO_IMAGE }]);
      setHeroLoaded(true);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const loadFeaturedProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products?limit=18&is_active=true&popularity=Más vendidos`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error loading featured products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTopProducts = async () => {
    try {
      // Cargar productos "Más vendidos" para Hombre
      const hombreResponse = await fetch(`${API_URL}/api/products?limit=15&is_active=true&gender=Hombre&popularity=Más vendidos`);
      if (hombreResponse.ok) {
        const hombreData = await hombreResponse.json();
        setTopHombreProducts(hombreData.products || []);
      }
      
      // Cargar productos "Más vendidos" para Mujer
      const mujerResponse = await fetch(`${API_URL}/api/products?limit=15&is_active=true&gender=Mujer&popularity=Más vendidos`);
      if (mujerResponse.ok) {
        const mujerData = await mujerResponse.json();
        setTopMujerProducts(mujerData.products || []);
      }
    } catch (error) {
      console.error('Error loading top products:', error);
    } finally {
      setTopLoading(false);
    }
  };

  const loadDiscoveryProducts = async () => {
    try {
      const results = {};
      for (const category of discoveryCategories) {
        try {
          // Use random_order with preference for Hombre
          const response = await fetch(`${API_URL}/api/products?${category.filter}&limit=15&is_active=true&random_order=true&prefer_gender=Hombre`);
          if (response.ok) {
            const data = await response.json();
            results[category.id] = data.products || [];
          }
        } catch (error) {
          console.error(`Error loading ${category.id}:`, error);
          results[category.id] = [];
        }
      }
      setDiscoveryProducts(results);
    } catch (error) {
      console.error('Error loading discovery products:', error);
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  return (
    <div className="bg-brand-bone min-h-screen" data-testid="home-page">
      {/* Hero Section with Carousel */}
      <section 
        className={`relative flex items-center justify-center overflow-hidden ${
          heroSlides[currentSlide]?.hide_overlay_text
            // Banner con arte propio (la promo va dentro de la imagen): en tablet la altura sigue la
            // proporción de la imagen de escritorio (~2.7:1) con un recorte lateral pequeño, así el
            // texto de la promo no se corta. En pantallas grandes (xl) vuelve a la altura de siempre.
            ? 'min-h-[80vh] sm:min-h-[70vh] md:min-h-0 md:aspect-[2.1/1] xl:aspect-auto xl:min-h-[70vh]'
            : 'min-h-[80vh] sm:min-h-[70vh] md:min-h-[70vh]'
        }`}
        style={{ backgroundColor: '#111118' }}
      >
        {/* Carousel Slides */}
        {heroLoaded && heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 z-0'
            }`}
          >
            {slide.media_type === 'video' ? (
              <video
                src={slide.media_url || slide.video_url}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <>
                {/* Desktop Image */}
                <img 
                  src={slide.image_url || slide.media_url || DEFAULT_HERO_IMAGE}
                  alt={slide.title || 'Banner'}
                  className="hidden md:block w-full h-full object-cover"
                />
                {/* Mobile Image - uses mobile_image_url if available, otherwise falls back to desktop */}
                <img 
                  src={slide.mobile_image_url || slide.image_url || slide.media_url || DEFAULT_HERO_IMAGE}
                  alt={slide.title || 'Banner'}
                  className="md:hidden w-full h-full object-cover"
                />
              </>
            )}
          </div>
        ))}
        
        {/* Overlay oscuro y poco opaco, solo para dar contraste al texto sobre la imagen */}
        {/* Banners con arte propio (hide_overlay_text) no se oscurecen: el diseño ya trae su contraste */}
        {!heroSlides[currentSlide]?.hide_overlay_text && (
          <div className="absolute inset-0 bg-brand-black/30 z-10 pointer-events-none" />
        )}

        {/* Content - Show by default, hide if banner has hide_overlay_text=true */}
        {(!heroSlides[currentSlide]?.hide_overlay_text) && (
          <div className="relative z-20 text-center px-6 max-w-4xl">
            <motion.h1 
              className="font-display text-5xl md:text-7xl tracking-tight leading-none mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-title"
            >
              <span className="text-white">Fragancias de</span>
              <br />
              <span className="text-brand-cobalt">Lujo</span>
            </motion.h1>
            <motion.p 
              className="text-lg md:text-xl leading-relaxed font-light text-white/80 mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              Perfumes inspirados en las mejores marcas del mundo
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link 
                to="/products" 
                className="brand-btn-bone inline-flex items-center gap-2"
                data-testid="explore-collection-btn"
              >
                <span>Explorar Colección</span>
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        )}

        {/* Carousel Indicators - hidden on mobile, only show when 2+ slides */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 hidden md:flex gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-brand-cobalt w-6' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Ir a slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Scroll indicator - hidden on mobile */}
        <motion.div 
          className="absolute inset-x-0 mx-auto w-fit bottom-8 z-20 hidden md:block"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-brand-cobalt/50 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-brand-cobalt rounded-full mt-2 animate-pulse" />
          </div>
        </motion.div>
      </section>

      {/* Quick Metrics Section - Sin espacio con el hero */}
      <QuickMetrics />

      {/* Educational Carousel - Nicho vs Diseñador */}
      <EducationalCarousel />

      {/* Mood Products Section - Pestañas por ocasión */}
      <MoodProductsSection />

      {/* Neon Banner */}
      <NeonBanner />

      {/* Trust Badges Section - OCULTO */}
      {/* <TrustBadges /> */}

      {/* UOMO & DONNE Section - OCULTO */}
      {/*
      <section className="pt-4 pb-8 md:pt-8 md:pb-10 px-4 md:px-12 max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-4 text-white">
            Despierta tu Alpha
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Descubre fragancias diseñadas para destacar tu personalidad
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/products?gender=Hombre"
              className="group block relative overflow-hidden rounded-lg"
              data-testid="uomo-section"
            >
              <img 
                src={UOMO_IMAGE}
                alt="Colección UOMO"
                className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
              />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-center">
                <p 
                  className="text-white text-sm md:text-base mb-3 font-medium"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,1), 0 4px 12px rgba(0,0,0,1), 0 0 30px rgba(0,0,0,0.9)' }}
                >
                  Fragancias con carácter y presencia
                </p>
                <span className="inline-flex items-center gap-2 px-5 py-2 bg-brand-cobalt text-white text-sm uppercase tracking-widest font-semibold group-hover:bg-white group-hover:text-brand-cobalt transition-all duration-300 rounded">
                  Explorar
                  <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/products?gender=Mujer"
              className="group block relative overflow-hidden rounded-lg"
              data-testid="donne-section"
            >
              <img 
                src={DONNE_IMAGE}
                alt="Colección DONNE"
                className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
              />
              
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-center">
                <p 
                  className="text-white text-sm md:text-base mb-3 font-medium"
                  style={{ textShadow: '0 2px 4px rgba(0,0,0,1), 0 4px 12px rgba(0,0,0,1), 0 0 30px rgba(0,0,0,0.9)' }}
                >
                  Esencias irresistibles y seductoras
                </p>
                <span className="inline-flex items-center gap-2 px-5 py-2 bg-brand-magenta text-white text-sm uppercase tracking-widest font-semibold group-hover:bg-white group-hover:text-brand-magenta transition-all duration-300 rounded">
                  Explorar
                  <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>
      */}

      {/* Top en Ventas Section - OCULTO */}
      {/*
      <section className="pt-6 pb-6 md:pt-10 md:pb-8 overflow-hidden" data-testid="top-ventas-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-8 md:mb-12 px-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-2xl md:text-5xl tracking-tight font-light mb-2 md:mb-4 text-brand-black">
              Top en Ventas
            </h2>
            <p className="text-sm md:text-base leading-relaxed text-brand-black/60 max-w-2xl mx-auto">
              Cada aroma cuenta una historia.
            </p>
          </motion.div>

          <ProductRow
            category={{
              id: 'top-hombre',
              title: <span>👔 Top de Ventas <strong className="font-bold">UOMO</strong></span>,
              emoji: '👔',
              filter: 'gender=Hombre'
            }}
            products={topHombreProducts}
            loading={topLoading}
          />

          <ProductRow
            category={{
              id: 'top-mujer',
              title: <span>👗 Top de Ventas <strong className="font-bold">DONNE</strong></span>,
              emoji: '👗',
              filter: 'gender=Mujer'
            }}
            products={topMujerProducts}
            loading={topLoading}
          />
        </div>
      </section>
      */}

      {/* Conoce nuestras familias olfativas - Netflix Style - OCULTO */}
      {/*
      <section className="pt-6 pb-6 md:pt-8 md:pb-8" data-testid="discover-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-8 md:mb-12 px-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-2xl md:text-5xl tracking-tight font-light mb-2 md:mb-4 text-brand-black">
              Conoce nuestras familias olfativas
            </h2>
            <p className="text-sm md:text-base text-brand-black/60 max-w-2xl mx-auto">
              Explora nuestras colecciones curadas para cada ocasión.
            </p>
          </motion.div>

          <div className="space-y-2">
            {discoveryCategories.map((category) => (
              <ProductRow
                key={category.id}
                category={category}
                products={discoveryProducts[category.id]}
                loading={discoveryLoading}
              />
            ))}
          </div>
        </div>
      </section>
      */}

      {/* All Reviews Section */}
      <AllReviewsSection />
    </div>
  );
};

export default Home;
